/**
 * Campos compuestos del back office editorial.
 *
 * Port de `templates/editorial-historias/Campo*.dc.html` del Design System Yool
 * (Claude Design, proyecto 1a4bd3e4-26b1-4642-b791-fe19abe4e2c9).
 *
 * CUATRO DE LOS SEIS SON SOLO COMPOSICION. `CampoTexto`, `CampoArea`,
 * `CampoSelect` y `CampoFecha` son `YoolField` envolviendo el control que el
 * sistema ya trae (`YoolInput`, `YoolTextarea`, `YoolSelect`, `YoolDateInput`):
 * no se dibuja nada a mano.
 *
 * LOS OTROS DOS NO EXISTEN EN EL SISTEMA. El stepper de `CampoNumero`, la fila
 * de muestras de `CampoColor` y la vista previa de `CampoImagen` no tienen
 * primitivo en el DS —se reviso el manifiesto de 50 componentes: lo unico
 * cercano es `YoolFileSlot`, que sube archivos y aca se guardan URLs—, y el
 * propio template los define como composicion local. El handoff es explicito en
 * que son azucar y no deben publicarse como componentes del sistema; por eso
 * viven en la carpeta de la seccion que los usa.
 *
 * DESVIACION DELIBERADA EN `CampoFecha`. El template implementa un calendario
 * propio de 131 lineas (popover de 272px, navegacion de mes, grilla de 7). Se
 * usa `YoolDateInput` en su lugar, porque el mismo handoff prohibe repintar a
 * mano un control que el sistema ya tiene. Si el calendario del template se
 * quiere de verdad, el camino es publicarlo como componente del DS y adoptarlo,
 * no copiarlo aca.
 *
 * ACCESO LAZY AL BUNDLE, igual que `formkit.jsx`: el bundle
 * (`window.DesignSystemRemix_1a4bd3`) se carga por script en `src/index.js` y
 * puede no estar listo en el primer render. Se lee `window` en cada render y, si
 * no esta, cada campo cae a los primitivos de formkit. Sin bundle la pantalla
 * sigue siendo usable; no queda un estado roto.
 */
import React from 'react';

import {
  Field,
  TextInput,
  TextArea,
  Select as SelectFormkit,
  FkPrimary,
  FkGhost,
  FkDanger,
  // `FkToggle` delega en el `Switch` de components/ui/switch.jsx, que es donde
  // vive la unica copia del diseno del DS. Se reusa en vez de envolver el
  // primitivo otra vez: dos envoltorios del mismo control se desincronizan.
  FkToggle,
} from '../../components/product_config/formkit';
// `RadioPills` se toma directo: es el primitivo, no hay nada que envolver.
import { RadioPills } from '../../components/ui/switch';

const DS = () =>
  (typeof window !== 'undefined' && window.DesignSystemRemix_1a4bd3) || {};

/**
 * Marca "estoy dentro de una `Fila`".
 *
 * Dentro de una grilla, el ancho lo decide la pista, no el campo. El `Field` de
 * caida pone `minWidth: 220` EN LINEA —gana a cualquier hoja de estilos— y en
 * una pista `minmax(0, 1fr)` mas angosta que eso el campo desborda su columna.
 * `full` es lo que apaga ese minimo (`minWidth: 0`), y con el bundle del DS no
 * cambia nada porque `.y-field` ya trae `min-width: 0` y el `flex` que agrega lo
 * ignora la grilla.
 *
 * Va por contexto y no repitiendo `full` en cada llamada porque es una
 * consecuencia de estar en la grilla, no una decision de cada campo.
 */
const ContextoFila = React.createContext(false);

/** `full` efectivo: el que pide quien llama, o el que impone la grilla. */
function useAncho(full) {
  // El hook va primero y aparte: `full || useContext(...)` cortocircuita, y un
  // hook que a veces no se llama rompe el orden entre renders.
  const enFila = React.useContext(ContextoFila);
  return full || enFila;
}

/* ── Boton ─────────────────────────────────────────────────────────────── */

/**
 * `YoolButton` con las variantes que el handoff fija para esta seccion, en un
 * solo lugar para que las reglas no queden repartidas por el formulario:
 *
 *   primary        Guardar historia / Nueva historia — UNO por vista
 *   ghost          Cancelar, Subir, Bajar
 *   outline        Agregar bloque / stat / producto
 *   danger-outline Quitar (secundario destructivo, borde rojo)
 *   destructive    Eliminar definitivo, solo dentro del modal
 *   link           Proponer slug, volver al listado
 *
 * El teal jamas aparece fuera de `primary`.
 *
 * Sin bundle cae a los botones de formkit, que solo tienen tres formas: el
 * mapeo agrupa `outline`/`link` en el ghost y los destructivos en el rojo.
 */
export function Boton({
  variant = 'primary', size = 'md', onClick, disabled, type = 'button', children,
}) {
  const { YoolButton } = DS();

  if (YoolButton) {
    return (
      <YoolButton
        variant={variant} size={size} onClick={onClick}
        disabled={disabled} type={type}
      >
        {children}
      </YoolButton>
    );
  }

  if (variant === 'primary') {
    return <FkPrimary onClick={onClick} disabled={disabled} type={type}>{children}</FkPrimary>;
  }
  if (variant === 'destructive' || variant === 'danger-outline') {
    return <FkDanger onClick={onClick} disabled={disabled} type={type}>{children}</FkDanger>;
  }
  return <FkGhost onClick={onClick} type={type} disabled={disabled}>{children}</FkGhost>;
}

/* ── Texto ─────────────────────────────────────────────────────────────── */

/**
 * Campo de texto de una linea. `YoolField` + `YoolInput`.
 *
 * @param {string}  label     Etiqueta (eyebrow 11px/mayusculas en el DS).
 * @param {string}  hint      Ayuda gris; el DS la oculta si hay error.
 * @param {string}  error     Mensaje de error: pinta el control y la etiqueta.
 * @param {boolean} required  Marca el asterisco.
 * @param {boolean} full      Ocupa el 100% del ancho de su fila.
 * @param {string}  basis     Base flex al compartir fila (default del DS: 220px).
 * @param {node}    action    Nodo a la derecha de la etiqueta (p. ej. un enlace).
 */
export function CampoTexto({
  label, hint, error, required, full, basis, action,
  id, name, value, onChange, placeholder, type = 'text', disabled, upper,
  ...resto
}) {
  const { YoolField, YoolInput } = DS();
  const ancho = useAncho(full);

  if (YoolField && YoolInput) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis} action={action} htmlFor={id}
      >
        <YoolInput
          {...resto}
          id={id} name={name} type={type} value={value ?? ''}
          onChange={onChange} placeholder={placeholder}
          disabled={disabled} upper={upper}
          state={error ? 'error' : 'default'}
        />
      </YoolField>
    );
  }

  return (
    <Field label={label} hint={error || hint} required={required} full={ancho} htmlFor={id}>
      <TextInput
        {...resto}
        id={id} name={name} type={type} value={value}
        onChange={onChange} placeholder={placeholder}
        disabled={disabled} upper={upper}
      />
    </Field>
  );
}

/* ── Area ──────────────────────────────────────────────────────────────── */

/** Texto multilinea. `YoolField` + `YoolTextarea`. */
export function CampoArea({
  label, hint, error, required, full = true, basis, action,
  id, name, value, onChange, placeholder, rows = 4, disabled,
  ...resto
}) {
  const { YoolField, YoolTextarea } = DS();
  const ancho = useAncho(full);

  if (YoolField && YoolTextarea) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis} action={action} htmlFor={id}
      >
        <YoolTextarea
          {...resto}
          id={id} name={name} value={value ?? ''} rows={rows}
          onChange={onChange} placeholder={placeholder} disabled={disabled}
          state={error ? 'error' : 'default'}
        />
      </YoolField>
    );
  }

  return (
    <Field label={label} hint={error || hint} required={required} full={ancho} htmlFor={id}>
      <TextArea
        {...resto}
        id={id} name={name} value={value} rows={rows}
        onChange={onChange} placeholder={placeholder} disabled={disabled}
      />
    </Field>
  );
}

/* ── Select ────────────────────────────────────────────────────────────── */

/**
 * Desplegable. `YoolField` + `YoolSelect`.
 *
 * `options` acepta `{value,label}` o string. Para permitir volver al valor
 * vacio hay que pasar la opcion vacia dentro de `options` —p. ej.
 * `{ value: '', label: 'Sin resaltado' }`— y no como `placeholder`: el
 * `placeholder` de formkit deja esa opcion `disabled` y el campo no vuelve
 * atras.
 */
export function CampoSelect({
  label, hint, error, required, full, basis, action,
  id, name, value, onChange, options = [], disabled,
  ...resto
}) {
  const { YoolField, YoolSelect } = DS();
  const ancho = useAncho(full);

  if (YoolField && YoolSelect) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis} action={action} htmlFor={id}
      >
        <YoolSelect
          {...resto}
          id={id} name={name} value={value ?? ''} options={options}
          onChange={onChange} disabled={disabled}
          state={error ? 'error' : 'default'}
        />
      </YoolField>
    );
  }

  return (
    <Field label={label} hint={error || hint} required={required} full={ancho} htmlFor={id}>
      <SelectFormkit
        {...resto}
        id={id} name={name} value={value} options={options}
        onChange={onChange} disabled={disabled}
      />
    </Field>
  );
}

/* ── Fecha ─────────────────────────────────────────────────────────────── */

/** Fecha. `YoolField` + `YoolDateInput` (ver la nota de desviacion arriba). */
export function CampoFecha({
  label, hint, error, required, full, basis,
  id, name, value, onChange, disabled,
  ...resto
}) {
  const { YoolField, YoolDateInput } = DS();
  const ancho = useAncho(full);

  if (YoolField && YoolDateInput) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis} htmlFor={id}
      >
        <YoolDateInput
          {...resto}
          id={id} name={name} type="date" value={value ?? ''}
          onChange={onChange} disabled={disabled}
          state={error ? 'error' : 'default'}
        />
      </YoolField>
    );
  }

  return (
    <Field label={label} hint={error || hint} required={required} full={ancho} htmlFor={id}>
      <TextInput
        {...resto}
        id={id} name={name} type="date" value={value}
        onChange={onChange} disabled={disabled}
      />
    </Field>
  );
}

/* ── Switch on/off ─────────────────────────────────────────────────────── */

/**
 * Interruptor con su etiqueta y su explicacion. Compone `FkToggle`, que ya
 * envuelve `YoolToggle` del DS (negro al encender; el teal queda para el unico
 * CTA primario de la vista).
 *
 * ORDEN Y MEDIDAS DEL TEMPLATE, no inventadas: el interruptor va PRIMERO y el
 * texto despues (`EditorialHistorias.dc.html`, bloque "Destacada en el home":
 * `display:flex; align-items:flex-start; gap:16px; padding:18px;
 * border:1px solid #E0E0E0; border-radius:14px`, titulo 14/600 y descripcion
 * 13/#54585A con `max-width:62ch`). Una version anterior de este archivo lo
 * ponia al reves, con el control a la derecha; era una invencion mia.
 *
 * Emite un BOOLEANO, no un evento: quien llama traduce a lo que guarda la
 * columna. `destacada` ya es booleano; cualquier otro campo se traduce en el
 * call site, no aca.
 */
export function CampoSwitch({ label, desc, checked, onChange, disabled, id }) {
  ponerEstilos();
  const idDesc = id && desc ? `${id}-desc` : undefined;
  return (
    <div className="ed-switch">
      <div style={{ flexShrink: 0 }} aria-describedby={idDesc}>
        <FkToggle on={Boolean(checked)} onChange={onChange} disabled={disabled} aria-label={label} />
      </div>
      <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
        <p
          id={id}
          style={{
            margin: 0, font: '600 14px/1.3 var(--font-sans)',
            letterSpacing: '-.01em', color: 'var(--yool-black, #1A1A1A)',
          }}
        >
          {label}
        </p>
        {desc ? (
          <p
            id={idDesc}
            style={{
              margin: 0, font: '400 13px/1.5 var(--font-sans)',
              color: 'var(--yool-gray1, #54585A)',
              maxWidth: '62ch', textWrap: 'pretty',
            }}
          >
            {desc}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ── Pills de opciones ─────────────────────────────────────────────────── */

/**
 * Eleccion entre dos o tres valores excluyentes: `YoolField` + `RadioPills`
 * en su variante con punto FancyYool (`dots`).
 *
 * DECISION EXPLICITA POR ENCIMA DEL TEMPLATE (3 de septiembre de 2026). Este
 * campo fue las dos cosas: `EditorialHistorias.dc.html` pide aqui
 * `YoolTabs variant="segmented"`, y el commit 07607236 revirtio las pills
 * precisamente por eso. Se vuelve a las pills a pedido, mirando la pantalla
 * desplegada. No es deriva ni un descuido: si alguien vuelve a comparar con el
 * template y ve que no calzan, esta linea es la respuesta.
 *
 * El punto conserva su color aun con el pill seleccionado en negro — ahi esta
 * lo distintivo de la variante. Los colores salen de la paleta curada de
 * `RadioPills` por posicion; una opcion puede fijar el suyo con `dot`.
 *
 * Emite el VALOR, no un evento, igual que `YoolTabs`.
 *
 * SIN `htmlFor`: las opciones son `<button role="radio">`, no un control con un
 * solo id, asi que un `<label for>` apuntaria a un elemento inexistente.
 *
 * Ya no depende del bundle: `RadioPills` trae el diseno del DS consigo, asi que
 * el campo se ve igual con bundle y sin el.
 * @see components/ui/switch.jsx
 */
export function CampoPills({
  label, hint, error, required, full, basis,
  id, value, onChange, options = [], disabled, dots = true,
}) {
  ponerEstilos();
  const { YoolField } = DS();
  const ancho = useAncho(full);

  const control = (
    <RadioPills
      value={value}
      options={options}
      onChange={onChange}
      dots={dots}
      disabled={disabled}
      aria-label={label}
    />
  );

  if (YoolField) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis}
      >
        {control}
      </YoolField>
    );
  }
  return (
    <Field label={label} hint={error || hint} required={required} full={ancho}>
      {control}
    </Field>
  );
}

/* ── Numero ────────────────────────────────────────────────────────────── */

const BOTON_STEPPER = {
  width: 36, height: '100%', border: 'none', background: '#fff',
  fontSize: 16, color: 'var(--yool-gray1, #54585A)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  transition: 'background .12s ease',
};

/**
 * Contador de enteros, con el diseno del template: 132px, menos / valor / mas,
 * y `—` cuando no hay valor.
 *
 * POR QUE UN STEPPER Y NO UN CAMPO DE TEXTO. El `orden` de una fila de producto
 * escrito a mano fue un defecto Critical: un valor vacio o decimal reventaba el
 * guardado DESPUES de insertar la historia y dejaba una fila fantasma ocupando
 * el slug. Se corrigio en `ordenANumero` y en el servicio, y las dos
 * validaciones se quedan como red del servidor — pero con este control el
 * defecto deja de ser alcanzable: no hay forma de escribir `1.5` ni texto.
 *
 * Emite siempre `{ target: { value } }` con un entero en string, para que el
 * `onChange` sea el mismo que usan los demas campos.
 */
export function CampoNumero({
  label, hint, error, required, full, basis,
  id, value, onChange, min = 1, disabled,
}) {
  const { YoolField } = DS();
  const ancho = useAncho(full);

  const crudo = value === '' || value == null ? null : parseInt(value, 10);
  const vacio = crudo === null || Number.isNaN(crudo);
  const enMinimo = !vacio && crudo <= min;
  const emitir = (v) => onChange && onChange({ target: { value: String(v) } });
  const idValor = id ? `${id}-valor` : undefined;

  const control = (
    <div
      style={{
        display: 'flex', alignItems: 'center', height: 40,
        border: '1.5px solid var(--yool-border, #E0E0E0)', borderRadius: 8,
        overflow: 'hidden', width: 132, opacity: disabled ? 0.5 : 1,
      }}
    >
      <button
        type="button"
        onClick={() => emitir(vacio ? min : Math.max(min, crudo - 1))}
        disabled={disabled || enMinimo}
        aria-label="Restar"
        aria-controls={idValor}
        className="ed-press"
        style={{
          ...BOTON_STEPPER,
          borderInlineEnd: '1px solid var(--yool-border, #E0E0E0)',
          cursor: disabled || enMinimo ? 'default' : 'pointer',
          opacity: enMinimo ? 0.4 : 1,
        }}
      >
        −
      </button>
      <span
        id={idValor}
        aria-live="polite"
        style={{
          flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 600,
          fontVariantNumeric: 'tabular-nums', color: 'var(--yool-black, #1A1A1A)',
        }}
      >
        {vacio ? '—' : crudo}
      </span>
      <button
        type="button"
        onClick={() => emitir(vacio ? min : crudo + 1)}
        disabled={disabled}
        aria-label="Sumar"
        aria-controls={idValor}
        className="ed-press"
        style={{
          ...BOTON_STEPPER,
          borderInlineStart: '1px solid var(--yool-border, #E0E0E0)',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        +
      </button>
    </div>
  );

  if (YoolField) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis}
      >
        {control}
      </YoolField>
    );
  }
  return (
    <Field label={label} hint={error || hint} required={required} full={ancho}>
      {control}
    </Field>
  );
}

/* ── Color ─────────────────────────────────────────────────────────────── */

/** Hex de cada token FancyYool, verbatim del template CampoColor.dc.html.
    Son los mismos que aplica el sitio publico: no se inventa ninguno. */
const HEX_FANCY = {
  teal: '#007D6E', cyan: '#06B6D4', blue: '#2563EB', sky: '#0EA5E9',
  violet: '#5B2BE5', indigo: '#7D7CFF', purple: '#A855F7', pink: '#E91E63',
  red: '#EF4444', orange: '#D35400', yellow: '#F5C400', lime: '#147D89',
  slate: '#64748B', gray: '#6B7280',
  'dark-teal': '#2DD4BF', 'dark-cyan': '#22D3EE', 'dark-blue': '#60A5FA',
  'dark-sky': '#38BDF8', 'dark-violet': '#A78BFA', 'dark-indigo': '#A5B4FC',
  'dark-purple': '#C084FC', 'dark-pink': '#F472B6', 'dark-red': '#FCA5A5',
  'dark-orange': '#FB923C', 'dark-lime': '#5EEAD4', 'dark-slate': '#CBD5E1',
  'dark-gray': '#CBD5E1', 'dark-yellow': '#F5C400',
};

/**
 * Elige el color del resaltado mostrando el color, no su nombre de token.
 *
 * Reemplaza al desplegable que listaba `dark-cyan`, `indigo`… y le pedia al
 * editor traducir de memoria un nombre a un color.
 *
 * La primera muestra, con borde punteado, es "sin resaltado" (valor vacio).
 * El estado seleccionado se comunica con `aria-pressed`, no solo con el anillo:
 * el color por si solo no es informacion accesible.
 */
export function CampoColor({
  label, hint, error, required, full, basis,
  value, onChange, options = [], disabled,
}) {
  const { YoolField } = DS();
  const ancho = useAncho(full);
  const seleccionado = value || '';

  const muestra = (bg, activo) => ({
    width: 26, height: 26, borderRadius: '50%', border: 'none', padding: 0,
    background: bg,
    cursor: disabled ? 'default' : 'pointer',
    boxShadow: activo
      ? `0 0 0 2px #fff, 0 0 0 4px ${bg}`
      : '0 0 0 1px rgba(26,26,26,.10)',
    transform: activo ? 'scale(1.1)' : 'scale(1)',
    transition: 'transform .12s cubic-bezier(0.2, 0, 0, 1)',
  });

  const control = (
    <div
      role="group"
      aria-label={label || 'Color del resaltado'}
      style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        minHeight: 26, paddingTop: 2, width: '100%', maxWidth: 314,
      }}
    >
      <button
        type="button"
        onClick={() => onChange && onChange({ target: { value: '' } })}
        disabled={disabled}
        title="Sin resaltado"
        aria-label="Sin resaltado"
        aria-pressed={seleccionado === ''}
        className="ed-press"
        style={{
          width: 26, height: 26, borderRadius: '50%', padding: 0,
          border: '1.5px dashed var(--yool-border, #C7C7C7)', background: '#fff',
          cursor: disabled ? 'default' : 'pointer',
          boxShadow: seleccionado === '' ? '0 0 0 2px #fff, 0 0 0 4px #EDEDED' : 'none',
          transition: 'box-shadow .12s ease',
        }}
      />
      {options
        .filter((o) => (typeof o === 'string' ? o : o.value))
        .map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const etiqueta = typeof o === 'string' ? o : o.label || o.value;
          const bg = HEX_FANCY[val] || '#B9BEC2';
          const activo = val === seleccionado;
          return (
            <button
              key={val}
              type="button"
              onClick={() => onChange && onChange({ target: { value: val } })}
              disabled={disabled}
              title={etiqueta}
              aria-label={etiqueta}
              aria-pressed={activo}
              className="ed-press"
              style={muestra(bg, activo)}
            />
          );
        })}
    </div>
  );

  if (YoolField) {
    return (
      <YoolField
        label={label} hint={hint} error={error} required={required}
        full={ancho} basis={basis}
      >
        {control}
      </YoolField>
    );
  }
  return (
    <Field label={label} hint={error || hint} required={required} full={ancho}>
      {control}
    </Field>
  );
}

/* ── Radio cards ───────────────────────────────────────────────────────── */

/**
 * Eleccion entre pocas opciones donde cada una necesita explicarse.
 * `YoolRadioCards` del DS (`y-radiocard`: borde negro al elegir, jamas teal).
 *
 * POR QUE NO UN DESPLEGABLE NI PILLS. Es el control del `tipo` de bloque del
 * cuerpo: "Párrafo", "Subtítulo" y "Cita destacada" no se distinguen por el
 * nombre —hay que saber que hace cada uno en la pagina publicada—, y un
 * `select` obliga a abrir la lista para ver que hay. Las pills muestran las
 * tres a la vez pero no tienen donde poner la explicacion. La tarjeta si.
 *
 * `options`: `[{ value, title, desc?, icon?, meta?, disabled? }]`.
 * Emite el VALOR, no un evento, igual que `CampoPills`.
 *
 * SIN `htmlFor`, por lo mismo que `CampoPills`: las opciones son
 * `<button role="radio">`, no un control con un solo id. La etiqueta se ata al
 * `role="radiogroup"` que dibuja el propio `YoolRadioCards` con
 * `aria-labelledby` — que si acepta, porque reenvia el resto de las props.
 */
export function CampoRadioCards({
  label, hint, error, required, full = true, basis,
  id, value, onChange, options = [], columns = 1, disabled,
}) {
  const { YoolField, YoolRadioCards } = DS();
  ponerEstilos();
  const ancho = useAncho(full);
  const idEtiqueta = id ? `${id}-label` : undefined;

  const control = YoolRadioCards ? (
    <YoolRadioCards
      value={value}
      options={options}
      onChange={onChange}
      columns={columns}
      disabled={disabled}
      aria-labelledby={idEtiqueta}
    />
  ) : (
    /* Sin bundle no hay `.y-radiocard`: esas reglas las inyecta el propio DS.
       Se repinta la tarjeta con las clases de esta seccion —mismas medidas y
       mismo negro— en vez de caer a `RadioPills`, que dibujaria pills sin
       lugar para la descripcion, que es justo lo que estas tarjetas aportan. */
    <div
      className="ed-rc"
      role="radiogroup"
      aria-labelledby={idEtiqueta}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={disabled || o.disabled}
          onClick={() => onChange && onChange(o.value)}
          className="ed-rc__card ed-press"
        >
          <span className="ed-rc__radio" aria-hidden="true"><span /></span>
          <span className="ed-rc__body">
            <span className="ed-rc__title">{o.title}</span>
            {o.desc ? <span className="ed-rc__desc">{o.desc}</span> : null}
          </span>
        </button>
      ))}
    </div>
  );

  // El id va en la ETIQUETA, no en el control: es lo que `aria-labelledby`
  // referencia desde el grupo.
  const etiqueta = idEtiqueta ? <span id={idEtiqueta}>{label}</span> : label;

  if (YoolField) {
    return (
      <YoolField
        label={etiqueta} hint={hint} error={error} required={required}
        full={ancho} basis={basis}
      >
        {control}
      </YoolField>
    );
  }
  return (
    <Field label={etiqueta} hint={error || hint} required={required} full={ancho}>
      {control}
    </Field>
  );
}

/* ── Posicion de la imagen ─────────────────────────────────────────────── */

/**
 * Las nueve posiciones del encuadre, verbatim de `CampoImagen.dc.html`.
 * El valor es el `object-position` que va a la columna y que el sitio aplica
 * tal cual sobre la foto.
 */
export const CELDAS_POSICION = [
  { valor: 'left top', titulo: 'Arriba izquierda' },
  { valor: 'center top', titulo: 'Arriba centro' },
  { valor: 'right top', titulo: 'Arriba derecha' },
  { valor: 'left center', titulo: 'Centro izquierda' },
  { valor: 'center', titulo: 'Centro' },
  { valor: 'right center', titulo: 'Centro derecha' },
  { valor: 'left bottom', titulo: 'Abajo izquierda' },
  { valor: 'center bottom', titulo: 'Abajo centro' },
  { valor: 'right bottom', titulo: 'Abajo derecha' },
];

/**
 * A que celda de la grilla corresponde un `object-position` guardado.
 *
 * Existe porque las historias publicadas tienen valores que la grilla no emite
 * —`center 30%` es el de la muestra— y hay que poder decir cual de las nueve
 * celdas los representa sin reescribir la columna: el porcentaje se agrupa en
 * tercios. Mismo `parsePos` del template.
 */
export function normalizarPosicion(valor) {
  const tokens = String(valor || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  let h = 1;
  let v = 1;
  tokens.forEach((t) => {
    if (t === 'left') h = 0;
    else if (t === 'right') h = 2;
  });
  tokens.forEach((t) => {
    if (t === 'top') v = 0;
    else if (t === 'bottom') v = 2;
    else if (/%$/.test(t)) {
      const n = parseFloat(t);
      v = n < 34 ? 0 : n < 67 ? 1 : 2;
    }
  });
  return CELDAS_POSICION[v * 3 + h];
}

/**
 * De donde se encuadra la foto, como una grilla de nueve.
 *
 * REEMPLAZA UN CAMPO DE TEXTO donde habia que escribir `center 30%` de memoria:
 * el editor no tiene por que conocer la sintaxis de `object-position`, y una
 * cadena mal escrita no da error — el navegador la ignora y la foto sale
 * centrada sin que nadie se entere. Las nueve celdas son todas las salidas
 * posibles del control, asi que no hay valor invalido que escribir.
 *
 * Emite `{ target: { value } }` para que el `onChange` sea el mismo que usan
 * los demas campos.
 */
export function CampoPosicionImagen({
  label = 'Posición de la imagen', id, value, onChange, disabled,
}) {
  ponerEstilos();
  const activa = normalizarPosicion(value);
  const idEtiqueta = id ? `${id}-label` : undefined;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span className="ed-pos__rotulo" id={idEtiqueta}>{label}</span>
      <div className="ed-pos" role="radiogroup" aria-labelledby={idEtiqueta}>
        {CELDAS_POSICION.map((c) => (
          <button
            key={c.valor}
            type="button"
            role="radio"
            aria-checked={c.valor === activa.valor}
            aria-label={c.titulo}
            title={c.titulo}
            disabled={disabled}
            onClick={() => onChange && onChange({ target: { value: c.valor } })}
            className="ed-pos__celda ed-press"
            data-on={c.valor === activa.valor ? 'si' : 'no'}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Imagen ────────────────────────────────────────────────────────────── */

/**
 * URL de imagen con su vista previa, y opcionalmente la posicion del encuadre.
 *
 * Es la diferencia entre editar un campo de texto y ver la foto que va a salir
 * publicada, con el `object-position` que se esta escribiendo aplicado.
 *
 * `YoolFileSlot` del DS no sirve aca: sube archivos (`File`) y el modelo guarda
 * URLs.
 */
export function CampoImagen({
  urlLabel = 'Imagen (URL)',
  posLabel = 'Posición de la imagen',
  hint,
  required,
  conPosicion = true,
  idUrl = 'imagen_url',
  idPos = 'imagen_posicion',
  urlValue,
  onUrlChange,
  posValue,
  onPosChange,
  disabled,
}) {
  ponerEstilos();
  const url = urlValue || '';
  const pos = posValue || 'center';

  return (
    <div className="ed-imagen">
    <div className="ed-imagen__grilla">
      <div style={{ display: 'grid', gap: 16 }}>
        <CampoTexto
          label={urlLabel} hint={hint} required={required} full
          id={idUrl} name={idUrl} value={url} onChange={onUrlChange}
          placeholder="https://.../foto.jpg" disabled={disabled}
        />
        {conPosicion ? (
          <CampoPosicionImagen
            label={posLabel} id={idPos} value={posValue}
            onChange={onPosChange} disabled={disabled}
          />
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        <span
          style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '.14em',
            textTransform: 'uppercase', color: 'var(--yool-muted, #8D8D8D)',
          }}
        >
          Vista previa
        </span>
        <div
          style={{
            width: 128, height: 96, borderRadius: 12,
            border: url ? 'none' : '1px solid var(--yool-border, #E0E0E0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
            background: 'var(--yool-page, #F7F7F7)', position: 'relative',
          }}
        >
          {url ? (
            <img
              src={url}
              alt=""
              className="ed-imagen__foto"
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                objectPosition: pos, display: 'block',
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 11, color: 'var(--yool-muted, #8D8D8D)',
                textAlign: 'center', padding: '0 10px',
              }}
            >
              Sin imagen
            </span>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

export default CampoTexto;

/* ── Estructura ────────────────────────────────────────────────────────── */

/* Las reglas de grilla y tarjeta se inyectan una sola vez, no por instancia:
   con estilo en linea no se puede escribir una media query. Mismo patron que
   usa `EditorialSplit` del sitio. */
let estilosPuestos = false;
function ponerEstilos() {
  if (estilosPuestos || typeof document === 'undefined') return;
  estilosPuestos = true;
  const s = document.createElement('style');
  s.dataset.editorial = 'estructura';
  s.textContent = `
.ed-pila { display: grid; gap: 24px; align-items: start; }
.ed-fila { display: grid; gap: 24px; align-items: start; }
.ed-fila--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.ed-fila--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.ed-fila--2-1 { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
.ed-tarjeta {
  border: 1px solid var(--yool-border, #E0E0E0);
  border-radius: 14px;
  padding: 18px;
  background: var(--white, #fff);
  display: grid;
  gap: 24px;
}
.ed-tarjeta + .ed-tarjeta { margin-block-start: 16px; }
.ed-tarjeta__cabecera {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.ed-tarjeta__num {
  font: 600 11px/1 var(--font-sans); letter-spacing: .08em;
  text-transform: uppercase; color: var(--yool-muted, #8D8D8D);
}
.ed-tarjeta__acciones { display: flex; gap: 8px; flex-wrap: wrap; }
/* Fila de switch, con las medidas del template (bloque "Destacada en el
   home"): gap 16, padding 18, radio 14, borde plano. El borde NO se enciende
   con el control — el estado ya lo dice el interruptor, que es negro cuando
   esta activo; encender tambien la caja era invento mio. */
.ed-switch {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 18px; border-radius: 14px;
  border: 1px solid var(--yool-border, #E0E0E0);
  background: var(--white, #fff);
}
/* El corte sale del contenido: por debajo de 900px dos campos de 220px mas su
   separacion ya no entran sin apretarse. */
/* 120ms y solo lo que cambia: son controles de alta frecuencia, una animacion
   propia ahi cobra su costo de atencion en cada clic. */
.ed-press {
  transition-property: scale, box-shadow, opacity;
  transition-duration: 120ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}
.ed-press:active:not(:disabled) { scale: 0.96; }
@media (prefers-reduced-motion: reduce) {
  .ed-press { transition: none; }
  .ed-press:active:not(:disabled) { scale: 1; }
}
/* La miniatura baja debajo de los campos cuando el hueco de texto se aprieta.
   Container query y no media query: este campo vive tanto en una columna del
   formulario como a ancho completo, asi que lo que manda es su propio ancho.
   La regla del container query va en su propia hoja: el parser CSS de jsdom
   no la entiende y descarta la hoja ENTERA donde aparezca.
 */
.ed-imagen { container-type: inline-size; }
.ed-imagen__grilla {
  display: grid; grid-template-columns: minmax(0, 1fr) 128px;
  gap: 24px; align-items: start;
}
/* Grilla de nueve del encuadre. Medidas verbatim de CampoImagen.dc.html: celda
   de 22px, separacion de 4, marco de 10 con radio 10. El width max-content es
   para que no se estire con la columna: es un control, no un campo de texto.
   OJO: esto vive dentro de un template literal de JS — nada de backticks. */
.ed-pos {
  display: grid;
  grid-template-columns: repeat(3, 22px);
  grid-template-rows: repeat(3, 22px);
  gap: 4px;
  padding: 10px;
  border: 1px solid var(--yool-border, #E0E0E0);
  border-radius: 10px;
  width: max-content;
  background: var(--white, #fff);
}
.ed-pos__rotulo {
  font-size: 10px; font-weight: 600; letter-spacing: .14em;
  text-transform: uppercase; color: var(--yool-muted, #8D8D8D);
}
.ed-pos__celda {
  width: 22px; height: 22px; border-radius: 6px; padding: 0;
  border: 1.5px solid var(--yool-border, #E0E0E0);
  background: var(--yool-page, #F7F7F7);
  cursor: pointer;
  transition: background .12s ease, border-color .12s ease;
}
/* Negro Apple al elegir, como el resto de la seleccion de esta pantalla: el
   teal queda para el unico CTA primario de la vista. */
.ed-pos__celda[data-on="si"] {
  background: var(--yool-black, #1A1A1A);
  border-color: var(--yool-black, #1A1A1A);
}
.ed-pos__celda:disabled { cursor: default; opacity: .5; }
@media (prefers-reduced-motion: reduce) { .ed-pos__celda { transition: none; } }
/* Tarjetas de seleccion SIN el bundle del DS. Con bundle manda la regla
   .y-radiocard, que inyecta el propio sistema; esto es solo la caida, con las
   mismas medidas para que la pantalla no cambie de forma segun cargue o no el
   script. Sin backticks: esto es un template literal de JS. */
.ed-rc { display: grid; gap: 12px; }
.ed-rc__card {
  display: flex; align-items: flex-start; gap: 13px; text-align: start; width: 100%;
  border: 1.5px solid var(--yool-border, #E0E0E0);
  border-radius: 12px; padding: 15px 16px; background: var(--white, #fff);
  cursor: pointer; font-family: var(--font-sans);
  transition: border-color .15s ease, box-shadow .15s ease;
}
.ed-rc__card:hover { border-color: var(--yool-muted, #8D8D8D); }
.ed-rc__card[aria-checked="true"] {
  border-color: var(--yool-black, #1A1A1A);
  box-shadow: inset 0 0 0 1px var(--yool-black, #1A1A1A);
}
.ed-rc__card:disabled { opacity: .45; cursor: not-allowed; }
.ed-rc__radio {
  flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%; margin-top: 1px;
  border: 1.5px solid var(--yool-border, #E0E0E0);
  display: flex; align-items: center; justify-content: center;
}
.ed-rc__card[aria-checked="true"] .ed-rc__radio {
  border-color: var(--yool-black, #1A1A1A); background: var(--yool-black, #1A1A1A);
}
.ed-rc__radio span { width: 7px; height: 7px; border-radius: 50%; background: #fff; opacity: 0; }
.ed-rc__card[aria-checked="true"] .ed-rc__radio span { opacity: 1; }
.ed-rc__body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ed-rc__title {
  display: block; font-size: 14px; font-weight: 600; letter-spacing: -.01em;
  color: var(--yool-black, #1A1A1A);
}
.ed-rc__desc {
  display: block; font-size: 12.5px; line-height: 1.5; margin-top: 2px;
  color: var(--yool-muted, #8D8D8D); text-wrap: pretty;
}
/* Contorno de 1px a baja opacidad: da profundidad sin ensuciar el borde. Negro
   puro, nunca un neutro teñido, que recoge el fondo y se lee como mugre. */
.ed-imagen__foto { outline: 1px solid oklch(0 0 0 / 0.1); outline-offset: -1px; }
@media (prefers-color-scheme: dark) {
  .ed-imagen__foto { outline-color: oklch(1 0 0 / 0.1); }
}
@media (max-width: 900px) {
  .ed-fila--2, .ed-fila--3, .ed-fila--2-1 { grid-template-columns: 1fr; }
}

/* ── Formulario con panel de previsualizacion al lado ──────────────────────
   OJO: esto vive dentro de un template literal de JS. Nada de backticks aca
   adentro — cierran la cadena y el archivo deja de parsear.

   El template del DS fija el panel en 560px. Aca crece: Lienzo dibuja a 1320px
   y escala, y a 560px la escala cae a 0.42 — por debajo del piso de 0.45, o
   sea scroll horizontal. Con clamp el panel toma lo que sobre hasta 860px,
   donde la escala ya es 0.65 y el cuerpo de 16px se lee a ~10px.

   El minimo de 560px se respeta igual: por debajo de 1240px de viewport las
   dos columnas se apilan y el panel pasa a ancho completo, que es cuando de
   verdad se ve la previsualizacion. Preferir eso a un panel lateral que ya no
   entra. */
.ed-form { display: flex; gap: 32px; align-items: flex-start; }
.ed-form__main { flex: 1 1 0; min-width: 0; display: grid; gap: 24px; }
.ed-form__preview {
  flex: 0 0 clamp(560px, 38%, 860px);
  min-width: 0;
  display: grid;
  gap: 24px;
  padding-block-end: 8px;
  /* Pegado mientras se edita: el punto del panel es mirarlo al escribir, y sin
     esto se queda arriba en cuanto el formulario baja. Los 76px dejan pasar la
     barra superior de tadmin. */
  position: sticky;
  top: 76px;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
}
@media (max-width: 1240px) {
  .ed-form { flex-direction: column; }
  .ed-form__preview {
    flex: 1 1 auto;
    width: 100%;
    position: static;
    max-height: none;
    overflow-y: visible;
  }
}
`;
  document.head.appendChild(s);

  const cq = document.createElement('style');
  cq.dataset.editorial = 'estructura-container';
  cq.textContent = `
@container (max-width: 420px) {
  .ed-imagen__grilla { grid-template-columns: minmax(0, 1fr); }
}
`;
  document.head.appendChild(cq);
}

/**
 * Pila vertical de campos.
 *
 * `Section` de formkit mete a sus hijos en un div sin `gap`, y el `Field` de
 * formkit no trae margen: dos campos seguidos quedaban pegados, sin aire entre
 * la ayuda de uno y la etiqueta del siguiente. La separacion entre grupos
 * (`Section`, 60px) es mas del doble que esta, que es lo que hace que se lean
 * como grupos.
 */
export function Pila({ children }) {
  ponerEstilos();
  return <div className="ed-pila">{children}</div>;
}

/**
 * Fila de campos en grilla explicita.
 *
 * Reemplaza al `Row` de formkit, que es `flex-wrap`: con flex cada campo cae
 * segun su `basis` de 220px, asi que las filas quedan dentadas y las columnas
 * no alinean entre filas. El diseno pide grillas explicitas justamente por eso.
 *
 * @param {'2'|'3'|'2-1'} reparto  Dos columnas iguales, tres iguales, o 2fr/1fr
 *                                 para el par URL + posicion.
 */
export function Fila({ reparto = '2', children }) {
  ponerEstilos();
  return (
    <ContextoFila.Provider value>
      <div className={`ed-fila ed-fila--${reparto}`}>{children}</div>
    </ContextoFila.Provider>
  );
}

/**
 * Tarjeta de un elemento repetido: bloque de cuerpo, fila de stat o de producto.
 *
 * Sin ella las acciones de un elemento se leen pegadas al campo del siguiente y
 * no se ve donde termina una unidad. El numero y las acciones van arriba; los
 * campos, abajo.
 */
export function TarjetaRepetidor({ numero, etiqueta = 'Bloque', acciones, children }) {
  ponerEstilos();
  return (
    <div className="ed-tarjeta">
      <div className="ed-tarjeta__cabecera">
        <span className="ed-tarjeta__num">
          {etiqueta} {numero}
        </span>
        {acciones ? <div className="ed-tarjeta__acciones">{acciones}</div> : null}
      </div>
      {children}
    </div>
  );
}

/* ── Resaltados ────────────────────────────────────────────────────────── */

/**
 * Los terminos a resaltar dentro de un texto.
 *
 * ANTES ERA UN CAMPO DE TEXTO, y ahi estaba el defecto: la frase se buscaba
 * entera dentro del texto. Para resaltar "Simulamos" y "contratamos" en
 * "Simulamos y contratamos el mismo dia" habia que escribir "Simulamos
 * contratamos", que como cadena no aparece — no se resaltaba nada, sin decir
 * por que. Mismo modelo que `palabrasDestacadas` en la configuracion de planes:
 * una fila por termino, cada una con su color opcional.
 *
 * `texto` es la cita o el titular donde se va a buscar: sirve para avisar en el
 * momento que un termino no aparece ahi, que es la unica forma de enterarse sin
 * publicar.
 */
export function CampoResaltados({
  label = 'Palabras a resaltar',
  hint,
  texto = '',
  colores = [],
  value,
  onChange,
  idBase = 'resaltado',
  disabled,
}) {
  ponerEstilos();
  const lista = Array.isArray(value) ? value : [];

  const emitir = (siguiente) => onChange && onChange(siguiente);
  const cambiar = (i, campo) => (e) =>
    emitir(lista.map((t, j) => (j === i ? { ...t, [campo]: e.target.value } : t)));
  const quitar = (i) => emitir(lista.filter((_, j) => j !== i));
  const agregar = () => emitir([...lista, { texto: '', color: '' }]);

  /* La comparacion es la misma que hace el sitio al dibujar: sin distinguir
     mayusculas y sobre el texto tal cual.

     Con el texto vacio no se avisa nada: en la banda de producto el titulo sale
     del catalogo cuando no hay override, asi que aqui no se sabe todavia contra
     que se va a buscar. Avisar ahi seria un falso positivo. */
  const sinTexto = !String(texto || '').trim();
  const aparece = (t) =>
    sinTexto || !t.trim() || String(texto).toLowerCase().includes(t.trim().toLowerCase());

  return (
    <div className="ed-pila">
      <div>
        <span className="ed-tarjeta__num">{label}</span>
        {hint ? (
          <p style={{ margin: '6px 0 0', font: '400 12px/1.45 var(--font-sans)', color: 'var(--yool-muted, #8D8D8D)' }}>
            {hint}
          </p>
        ) : null}
      </div>

      {lista.map((termino, i) => (
        <TarjetaRepetidor key={i} etiqueta="Término" numero={i + 1}
          acciones={
            <Boton variant="danger-outline" size="sm" disabled={disabled}
              onClick={() => quitar(i)}>Quitar</Boton>
          }>
          <Fila reparto="2-1">
            <CampoTexto
              label="Palabra o frase"
              id={`${idBase}-texto-${i}`}
              value={termino.texto}
              onChange={cambiar(i, 'texto')}
              disabled={disabled}
              placeholder="Simulamos"
              error={aparece(termino.texto) ? undefined : 'No aparece en el texto: se dibuja sin resaltar.'}
            />
            <CampoColor
              label="Color"
              hint="Vacío usa el color del bloque."
              value={termino.color}
              onChange={cambiar(i, 'color')}
              options={colores}
              disabled={disabled}
            />
          </Fila>
        </TarjetaRepetidor>
      ))}

      {lista.length === 0 ? (
        <p style={{ margin: 0, font: '400 13px/1.5 var(--font-sans)', color: 'var(--yool-muted, #8D8D8D)' }}>
          Sin términos: el texto se dibuja sin resaltado.
        </p>
      ) : null}

      <div>
        <Boton variant="outline" size="sm" onClick={agregar} disabled={disabled}>
          Agregar término
        </Boton>
      </div>
    </div>
  );
}
